import type { ColumnDef } from '@tanstack/react-table';
import dayjs from 'dayjs';
import {
  AlertCircle,
  BatteryFull,
  BatteryWarning,
  BellOff,
  PlugZap,
} from 'lucide-react';
import { useMemo } from 'react';

import ArrowTooltip from '@/components/custom-ui/customTooltip';
import { Image } from '@/components/custom-ui/Image';
import { createIDColumn } from '@/components/custom-ui/TableColumns/IDColumn';
import { StyledCellColumn } from '@/components/custom-ui/TableColumns/StyledCell';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import type { ItemUnderRequest } from '@/pages/PurchasesContainer/PurchasesRequests/api/Request.interface.ts';
import DiscountMap from '@/pages/PurchasesContainer/RequestPerOffer/components/PrepareRequest/itemsTable/columns/DiscountCol/DiscountMap';
import { useDialogStore } from '@/store';
import { useAppTheme } from '@/theme/themeProvider';
import AvatarToolTip from '@/UI/AvatarToolTip';
import { DataTable } from '@/UI/TableShadcn';

import ActionBtn from './ActionBtn';
import { SimpleCalculationsButton } from './SimpleCalculationsButton';

type Props = {
  data: ItemUnderRequest[];
};

// Helper function to determine if an item needs approval
const itemNeedsApproval = (
  item: ItemUnderRequest,
): { needsApproval: boolean; reasons: string[] } => {
  // Check discount status
  const hasHighDiscount = item.DiscountStatus === 'bad';
  const hasRetailNeedStock = item.RetailStatus === 'overStock';
  const hasWholesaleNeedStock = item.WholeSaleStatus === 'overStock';
  const hasRetailCanTransfer = item.RetailStatus === 'canTransfer';
  const hasWholesaleCanTransfer = item.WholeSaleStatus === 'canTransfer';
  const hasUnderRequest = item.UnderRequest > 0;

  // Build reasons as array of strings
  const reasons: string[] = [];

  if (hasHighDiscount) {
    reasons.push('الخصم سيْ جدًا');
  }
  if (hasRetailNeedStock) {
    reasons.push('رصيد قطاعي زيادة');
  }
  if (hasWholesaleNeedStock) {
    reasons.push('رصيد جملة زيادة');
  }
  if (hasRetailCanTransfer) {
    reasons.push('تحويل من الجملة الي قطاعي');
  }
  if (hasWholesaleCanTransfer) {
    reasons.push('تحويل من القطاعي للجملة');
  }
  if (hasUnderRequest) {
    reasons.push(`تحت الطلب ${item.UnderRequest}`);
  }

  // Consider items with high discounts combined with critical stock as needing approval
  const needsApproval =
    hasHighDiscount ||
    hasRetailNeedStock ||
    hasWholesaleNeedStock ||
    hasRetailCanTransfer ||
    hasWholesaleCanTransfer ||
    hasUnderRequest;

  return {
    needsApproval,
    reasons: reasons.length ? reasons : ['يحتاج إلى موافقة'],
  };
};

// Helper component for store status
function StoreStatusBadge({
  status,
  label,
}: {
  status: string;
  label: string;
}) {
  const typeMapping: Record<
    string,
    { tooltip: string; bg: string; text: string; icon: React.ReactNode }
  > = {
    needStock: {
      tooltip: `${label} : 🚨 يحتاج رصيد 😱 🆘`,
      bg: 'bg-red-100/80 dark:bg-red-950/80',
      text: 'text-red-800 dark:text-red-200',
      icon: <BatteryWarning className="h-4 w-4" />,
    },
    canTransfer: {
      tooltip: `${label} : 🔌 ⚡️ التحويل من مخزن 🔄`,
      bg: 'bg-yellow-100/80 dark:bg-yellow-950/80',
      text: 'text-yellow-800 dark:text-yellow-200',
      icon: <PlugZap className="h-4 w-4" />,
    },
    noTrans: {
      tooltip: `${label} : 🚫 لا يوجد حركة 😴`,
      bg: 'bg-gray-100/80 dark:bg-gray-900/80',
      text: 'text-gray-800 dark:text-gray-300',
      icon: <BellOff className="h-4 w-4" />,
    },
    overStock: {
      tooltip: `${label} : 💰 رصيد زيادة 🔥`,
      bg: 'bg-green-200/30 dark:bg-green-400/30',
      text: 'text-green-700 dark:text-green-100',
      icon: <BatteryFull className="h-4 w-4" />,
    },
    default: {
      tooltip: `${label} : غير معروف`,
      bg: 'bg-gray-100/80 dark:bg-gray-900/80',
      text: 'text-gray-800 dark:text-gray-300',
      icon: <BellOff className="h-4 w-4" />,
    },
  };

  const statusInfo = typeMapping[status] || typeMapping.default;

  return (
    <ArrowTooltip color="primary" msg={statusInfo.tooltip}>
      <div
        className={`flex h-6 w-6 items-center justify-center rounded-md border border-primary px-0.5 ${statusInfo.bg} ${statusInfo.text}`}
      >
        {statusInfo.icon}
      </div>
    </ArrowTooltip>
  );
}

function UnderRequestTable({ data }: Props) {
  const { isDark } = useAppTheme();
  const { openClientCardDialog } = useDialogStore();
  const columns = useMemo<ColumnDef<ItemUnderRequest>[]>(
    () => [
      createIDColumn<ItemUnderRequest>(),
      {
        accessorKey: 'User',
        header: 'موظف',
        cell: ({ row }) => {
          const item = row.original;
          const { needsApproval } = itemNeedsApproval(item);

          return (
            <div
              className={cn(
                'flex h-10 w-full items-center justify-center border border-l-0 border-t-0',
                needsApproval && 'bg-red-50/80 dark:bg-red-950/20',
              )}
            >
              {item.User && (
                <AvatarToolTip
                  size={30}
                  variant="rounded"
                  avatar={item.User.avatar}
                  empName={item.User.empName}
                  id={item.User._id}
                />
              )}
            </div>
          );
        },
      },
      {
        accessorKey: 'Client',
        header: 'مورد',
        cell: ({ row }) => {
          const item = row.original;
          const { needsApproval } = itemNeedsApproval(item);

          return (
            <div
              className={cn(
                'flex h-auto min-h-10 w-full flex-col items-start justify-center gap-0.5 border border-l-0 border-t-0 px-2',
                needsApproval && 'bg-red-50/80 dark:bg-red-950/20',
              )}
            >
              {item.Client && (
                <>
                  <Button
                    variant="link"
                    className="h-5 p-0 text-left text-[15px] hover:text-yellow-600"
                    onClick={() => {
                      if (item.Client?.ID) {
                        openClientCardDialog(item.Client.ID);
                      }
                    }}
                  >
                    {item.Client.ClientName || '-'}
                  </Button>
                  <div className="flex flex-col gap-1">
                    <div className="flex items-center gap-1.5">
                      <Badge
                        variant="outline"
                        className="border-blue-200 bg-blue-50 px-1 py-0 text-[10px] text-blue-700 dark:border-blue-800 dark:bg-blue-900/20 dark:text-blue-300"
                      >
                        #{item.Client.ID || '?'}
                      </Badge>
                      <span className="line-clamp-1 font-sans text-xs text-gray-600 dark:text-gray-400">
                        فئة: {item.Client.suppliarClass || '-'}
                      </span>
                      {item.TrDate && (
                        <div className="flex items-center gap-1">
                          <Badge
                            variant="soft"
                            className="border-green-200 bg-green-50 px-1.5 py-0.5 text-[12px] text-green-700 dark:border-green-800 dark:bg-green-900/20 dark:text-green-300"
                          >
                            {dayjs(item.TrDate).format('YYYY-MM-DD')}
                          </Badge>
                          <Badge
                            variant="soft"
                            className="border-purple-200 bg-purple-50 px-1.5 py-0.5 text-[12px] text-purple-700 dark:border-purple-800 dark:bg-purple-900/20 dark:text-purple-300"
                          >
                            {dayjs(item.TrDate).format('hh:mm A')}
                          </Badge>
                        </div>
                      )}
                    </div>
                  </div>
                </>
              )}
            </div>
          );
        },
      },
      {
        accessorKey: 'itemName',
        header: 'الصنف',
        cell: ({ row }) => {
          const item = row.original;
          const { needsApproval, reasons } = itemNeedsApproval(item);

          // Format reasons as HTML list with better styling
          const formattedReasons = `
            <div dir="rtl" class="font-alex bg-white dark:bg-gray-900 p-3 rounded-md">
              <div class="font-beiruti text-lg text-red-600 dark:text-red-400 mb-2 pb-1 border-b border-red-200 dark:border-red-800 font-bold flex items-center">
                <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 ml-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                أسباب المراجعة
              </div>
              <ul class="list-disc mr-5 space-y-1.5 text-sm">
                ${reasons.map((reason) => `<li class="font-sans text-gray-800 dark:text-gray-200">${reason}</li>`).join('')}
              </ul>
            </div>
          `;

          return (
            <div
              className={cn(
                'flex h-auto min-h-10 w-full flex-col items-start justify-start border border-l-0 border-t-0',
                needsApproval && 'bg-red-50/80 dark:bg-red-950/20',
              )}
            >
              <div
                className={cn(
                  'line-clamp-1 flex w-full items-center gap-1 px-2 font-medium',
                  needsApproval && 'text-red-700 dark:text-red-400',
                )}
              >
                {needsApproval && (
                  <ArrowTooltip
                    color="destructive"
                    msg={formattedReasons}
                    isHtml={true}
                  >
                    <div>
                      <AlertCircle className="h-3.5 w-3.5" />
                    </div>
                  </ArrowTooltip>
                )}
                {item.itemName || 'غير متوفر'}
              </div>

              {item.MainID && (
                <div className="mb-1 mt-0.5 px-2">
                  <div className="inline-flex items-center rounded-sm border border-primary/30 bg-primary/5 px-1.5 py-0.5 text-xs font-normal text-primary">
                    <span className="opacity-70">الرقم:</span>
                    <span className="mr-0.5 font-medium">{item.MainID}</span>
                  </div>
                </div>
              )}
            </div>
          );
        },
      },
      {
        accessorKey: 'Qty',
        header: 'كمية',
        cell: ({ row }) => {
          const item = row.original;
          const { needsApproval } = itemNeedsApproval(item);

          return (
            <div
              className={cn(
                'flex h-10 w-full border border-l-0 border-t-0',
                needsApproval && 'bg-red-50/80 dark:bg-red-950/20',
              )}
            >
              <StyledCellColumn
                className="m-0 h-full w-full"
                value={item.Qty || 0}
              />
            </div>
          );
        },
      },
      {
        accessorKey: 'Price',
        header: 'جمهور',
        cell: ({ row }) => {
          const item = row.original;
          const { needsApproval } = itemNeedsApproval(item);

          return (
            <div
              className={cn(
                'flex h-10 w-full border border-l-0 border-t-0',
                needsApproval && 'bg-red-50/80 dark:bg-red-950/20',
              )}
            >
              <StyledCellColumn
                className="m-0 h-full w-full"
                color="no-color"
                value={item.Price || 0}
                comma={2}
              />
            </div>
          );
        },
      },
      {
        accessorKey: 'Discount',
        header: 'خصم',
        cell: ({ row }) => {
          const item = row.original;
          const discountStatus = item.DiscountStatus;
          const { needsApproval } = itemNeedsApproval(item);

          return (
            <div
              className={cn(
                'flex h-10 w-full items-center justify-center border border-l-0 border-t-0',
                needsApproval && 'bg-red-50/80 dark:bg-red-950/20',
              )}
            >
              <StyledCellColumn
                className="m-0 h-full w-full rounded-none border-l p-0"
                color={
                  discountStatus === 'best'
                    ? 'green'
                    : discountStatus === 'accept'
                      ? 'gray'
                      : discountStatus === 'bad'
                        ? 'red'
                        : discountStatus === 'only'
                          ? 'indigo'
                          : 'fuchsia'
                }
                comma={3}
                value={item.Discount || 0}
              />
              <DiscountMap discountStatus={discountStatus} />
            </div>
          );
        },
      },
      {
        accessorKey: 'Bonus',
        header: 'بونص',
        cell: ({ row }) => {
          const item = row.original;
          const { needsApproval } = itemNeedsApproval(item);

          return (
            <div
              className={cn(
                'flex h-10 w-full border border-l-0 border-t-0',
                needsApproval && 'bg-red-50/80 dark:bg-red-950/20',
              )}
            >
              <StyledCellColumn
                className="m-0 h-full w-full"
                color="green"
                value={item.Bonus || 0}
                comma={2}
              />
            </div>
          );
        },
      },
      {
        accessorKey: 'Tax',
        header: 'ض',
        cell: ({ row }) => {
          const item = row.original;
          const { needsApproval } = itemNeedsApproval(item);

          return (
            <div
              className={cn(
                'flex h-10 w-full border border-l-0 border-t-0',
                needsApproval && 'bg-red-50/80 dark:bg-red-950/20',
              )}
            >
              <StyledCellColumn
                className="m-0 h-full w-full"
                color="no-color"
                value={item.Tax || 0}
                comma={2}
              />
            </div>
          );
        },
      },
      {
        accessorKey: 'BNO',
        header: 'تشغيلة',
        cell: ({ row }) => {
          const item = row.original;
          const { needsApproval } = itemNeedsApproval(item);

          return (
            <div
              className={cn(
                'flex h-11 w-full flex-col items-center justify-center gap-1 border border-l-0 border-t-0 px-1 py-0.5',
                needsApproval && 'bg-red-50/80 dark:bg-red-950/20',
              )}
            >
              <div className="line-clamp-1 flex w-full items-center justify-center rounded-md border border-emerald-200 bg-emerald-50 px-1 py-1 text-center font-sans text-sm font-semibold tracking-wider text-emerald-800 shadow-sm dark:border-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300">
                <span className="mx-auto mt-1 text-center">
                  {item.BNO || '-'}
                </span>
              </div>
              {item.ExpireDate && (
                <div className="line-clamp-1 flex w-full items-center justify-center rounded-md border border-amber-200 bg-amber-50 px-1 py-1 text-center font-sans text-sm font-semibold tracking-wider text-amber-800 shadow-sm dark:border-amber-800 dark:bg-amber-900/30 dark:text-amber-300">
                  <span className="mx-auto mt-1 text-center">
                    {item.ExpireDate || '-'}
                  </span>
                </div>
              )}
            </div>
          );
        },
      },
      {
        accessorKey: 'RetailStatus',
        header: 'حالة',
        cell: ({ row }) => {
          const item = row.original;
          const retailStatus = item.RetailStatus || '';
          const wholeSaleStatus = item.WholeSaleStatus || '';
          const { needsApproval } = itemNeedsApproval(item);

          // Return status badges
          return (
            <div
              className={cn(
                'flex h-10 w-full items-center justify-center gap-0.5 border border-l-0 border-t-0',
                needsApproval && 'bg-red-50/80 dark:bg-red-950/20',
              )}
            >
              <StoreStatusBadge status={retailStatus} label="قطاعي" />
              <StoreStatusBadge status={wholeSaleStatus} label="جملة" />
            </div>
          );
        },
      },
      {
        id: 'calculations',
        header: 'حسابات',
        maxSize: 80,
        size: 70,
        cell: ({ row }) => {
          const item = row.original;
          const { needsApproval } = itemNeedsApproval(item);

          return (
            <div
              className={cn(
                'flex h-10 w-full items-center justify-center border border-l-0 border-t-0',
                needsApproval && 'bg-red-50/80 dark:bg-red-950/20',
              )}
            >
              <div className="flex w-full justify-center">
                <SimpleCalculationsButton item={item} />
              </div>
            </div>
          );
        },
      },
      {
        accessorKey: '_id',
        header: '',
        width: 30,
        cell: ({ row }) => {
          return (
            <div className="flex w-full items-center justify-center">
              <ActionBtn
                id={row?.original?.PurchaseRequestHead || row?.original?._id}
              />
            </div>
          );
        },
      },
    ],
    [openClientCardDialog],
  );

  return (
    <div className="p-4">
      {data.length > 0 ? (
        <DataTable
          columns={columns}
          data={data}
          isPaginated
          isTopToolbar
          isRowSelection
          heightTable="50vh"
        />
      ) : (
        <div className="flex flex-col items-center justify-center">
          <div className="flex h-[77vh] flex-col items-center justify-center">
            <div className="grid place-content-center rounded">
              <Image
                alt="no_data"
                src={
                  isDark ? '/images/svg/empty1.svg' : '/images/svg/empty3.svg'
                }
                isBlurred
                className="w-[200px]"
              />
            </div>
            <h3 className="mt-5 font-alex text-2xl text-muted">
              لا توجد طلبات شراء نشطة
            </h3>
          </div>
        </div>
      )}
    </div>
  );
}

export default UnderRequestTable;
